// Shared "lead capture checklist" computation. Extracted from
// `ChecklistCard.tsx` so both the in-lead checklist card (FullLeadView) and
// the new activity-dock active-lead checklist section
// (`ActivityDockActiveLeadChecklistSection`) consume the same source of
// truth — see docs/planning/org_activity_floating_dock.md §11.
//
// Pure functions only: no React, no DOM. Component-level styling +
// animation behavior live in the consumers.

import type { ExtractedLeadV2 } from "../../../types/lead";

export type LeadChecklistItemKey =
  | "contact_info"
  | "product_line"
  | "lead_source"
  | "budget";

export type LeadChecklistItemStatus = "ok" | "missing" | "warning";

export type LeadChecklistItem = {
  key: LeadChecklistItemKey;
  status: LeadChecklistItemStatus;
  /** Human-readable label, including emoji prefix. Matches the legacy ChecklistCard copy. */
  label: string;
};

/**
 * Compute the four-row capture checklist for a lead's extracted fields.
 * Order matches the legacy ChecklistCard layout: contact → product →
 * source → budget. `ok` = green, `missing` = red (hard miss), `warning`
 * = yellow (soft miss / optional).
 *
 * `extracted` is allowed to be `null` — that returns four "missing"/"warning"
 * rows in the canonical order, the same shape the dock section consumes
 * during the "lead just opened, no data yet" window.
 */
export function computeLeadChecklistItems(
  extracted: ExtractedLeadV2 | null,
): LeadChecklistItem[] {
  const e = extracted;
  const hasContact = !!(e?.first_name && e?.phone);
  const hasProduct = !!e?.product_line;
  const hasSource = !!e?.lead_source_category;
  const hasBudget = !!e?.enunciated_budget;

  return [
    {
      key: "contact_info",
      status: hasContact ? "ok" : "missing",
      label: hasContact
        ? "✅ Contact info captured"
        : "❌ Missing name or phone",
    },
    {
      key: "product_line",
      status: hasProduct ? "ok" : "missing",
      label: hasProduct
        ? `✅ Product line: ${e?.product_line}`
        : "❌ No product line detected",
    },
    {
      key: "lead_source",
      status: hasSource ? "ok" : "warning",
      label: hasSource
        ? `✅ Lead source: ${e?.lead_source_category}`
        : "⚠️ Lead source not clear",
    },
    {
      key: "budget",
      status: hasBudget ? "ok" : "warning",
      label: hasBudget
        ? "✅ Budget captured"
        : "⚠️ No spoken budget found",
    },
  ];
}

/** True when at least one item is `missing` or `warning` (i.e. not all-green). */
export function hasOutstandingChecklistItems(items: LeadChecklistItem[]): boolean {
  return items.some((it) => it.status !== "ok");
}
